/**
 * Beloved — Entitlements (Beloved Plus)
 *
 * The ONLY thing this service gates is the personal, made-for-you prayer at the
 * end of "Help Me Pray". Everything else in Beloved — the daily verse, morning
 * and evening prayer, themed prayers, biblical prayers, and saving — is free
 * forever and is never touched by this module.
 *
 * Free tier: one personal prayer to try, then a free Beloved account opens the
 * usual 3 per calendar month. The monthly count resets on its own when the
 * month changes (we remember a "monthKey" like "2026-07" and start fresh
 * whenever it differs); the try-it prayer is once ever on a device and never
 * comes back. Beloved Plus: personal prayers whenever needed, protected by
 * generous server-side fair-use limits.
 *
 * This module is safe to import on both web and native. Each RevenueCat SDK is
 * dynamically imported only on its own platform and only when its public key is
 * configured. A hidden developer flag still lets us test the paid state without
 * opening a real checkout.
 */

import { Capacitor } from '@capacitor/core';

/**
 * localStorage key holding
 * `{ tier, aiPrayersUsedThisMonth, anonPrayersUsedEver, monthKey }`.
 */
export const STORAGE_KEY = 'beloved_entitlements';

/** Personal prayers a signed-in free person may create each calendar month. */
export const FREE_AI_PRAYER_LIMIT = 3;

/**
 * Personal prayers someone may create before Beloved asks for a free account.
 * Unlike the monthly allowance this is once ever on a device: it exists so a
 * person can feel what a personal prayer is before deciding to sign in.
 */
export const ANONYMOUS_AI_PRAYER_LIMIT = 1;

/** Anonymous, device-local identifiers used only for server fair-use checks. */
export const PRAYER_CLIENT_ID_KEY = 'beloved_prayer_client_id';
export const REVENUECAT_APP_USER_ID_KEY = 'beloved_revenuecat_app_user_id';

/**
 * RevenueCat public SDK key for iOS, supplied at build time. RevenueCat public
 * app keys are designed to ship in the client; secret REST API keys must never
 * use this variable. While it is empty, native purchases resolve as
 * "unavailable" rather than throwing, so preview builds stay safe.
 */
export const REVENUECAT_IOS_KEY =
  (process.env.REACT_APP_REVENUECAT_IOS_API_KEY || '').trim();

/** RevenueCat Web Billing public SDK key. Secret `sk_` keys never belong here. */
export const REVENUECAT_WEB_KEY =
  (process.env.REACT_APP_REVENUECAT_WEB_API_KEY || '').trim();

/** RevenueCat offering and the two products inside it. */
export const OFFERING_ID = 'beloved_plus';
export const ENTITLEMENT_ID = 'beloved_plus';
export const PRODUCT_ANNUAL = 'plus_annual';
export const PRODUCT_MONTHLY = 'plus_monthly';

/**
 * Safe display prices used on the web and before the native store responds.
 * Native builds replace these with RevenueCat's localized prices, including
 * introductory-offer eligibility, so the paywall never assumes GBP abroad.
 */
export const FALLBACK_PLUS_PLANS = Object.freeze([
  Object.freeze({
    id: PRODUCT_ANNUAL,
    headlinePrice: '\u00A339.99',
    cadence: 'a year',
    billing: '\u00A33.33/month equivalent \u00B7 renews annually',
    checkoutLabel: 'Continue \u2014 \u00A339.99/year',
    saving: 'SAVE 44%',
    best: true,
  }),
  Object.freeze({
    id: PRODUCT_MONTHLY,
    headlinePrice: '\u00A35.99',
    cadence: 'a month',
    billing: 'Renews monthly',
    checkoutLabel: 'Continue \u2014 \u00A35.99/month',
    saving: null,
    best: false,
  }),
]);

/**
 * Hidden developer flag. When `localStorage.beloved_dev_plus === 'true'`,
 * `purchase()` resolves success and turns on Beloved Plus, so the paid state
 * can be exercised on the web during testing.
 */
export const DEV_PLUS_FLAG = 'beloved_dev_plus';

/**
 * The calendar-month key for a date, e.g. "2026-07". Used to know when the
 * free monthly allowance should reset.
 * @param {Date} [date] the date to key on (defaults to now)
 * @returns {string} a "YYYY-MM" month key
 */
export function getMonthKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * A fresh, default entitlements state for the current month (free tier).
 * @returns {{tier: 'free'|'plus', aiPrayersUsedThisMonth: number, anonPrayersUsedEver: number, monthKey: string}}
 */
function defaultState() {
  return {
    tier: 'free',
    aiPrayersUsedThisMonth: 0,
    anonPrayersUsedEver: 0,
    monthKey: getMonthKey(),
  };
}

/**
 * Read a stored counter as a whole, non-negative number. Anything missing or
 * malformed counts as zero.
 * @param {unknown} value
 * @returns {number}
 */
function countFrom(value) {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

/**
 * Read (and quietly repair) the stored entitlements state.
 *
 * If the stored month differs from the current month, the monthly personal-
 * prayer counter resets to zero and the new month is written back. The
 * anonymous try-it counter is deliberately never reset. Any storage or parse
 * failure falls back to a safe free-tier default. Never throws.
 *
 * @returns {{tier: 'free'|'plus', aiPrayersUsedThisMonth: number, anonPrayersUsedEver: number, monthKey: string}}
 */
export function readState() {
  let stored = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    stored = raw ? JSON.parse(raw) : null;
  } catch (e) {
    stored = null;
  }

  if (!stored || typeof stored !== 'object') {
    const fresh = defaultState();
    writeState(fresh);
    return fresh;
  }

  const tier = stored.tier === 'plus' ? 'plus' : 'free';
  const currentMonth = getMonthKey();
  const used = countFrom(stored.aiPrayersUsedThisMonth);

  // State written before the account gate existed carries no lifetime counter.
  // Someone who had already received a personal prayer has had their one taste
  // of it, so carry that across instead of handing everyone a fresh try-it.
  const anonUsed = Object.prototype.hasOwnProperty.call(stored, 'anonPrayersUsedEver')
    ? countFrom(stored.anonPrayersUsedEver)
    : Math.min(used, ANONYMOUS_AI_PRAYER_LIMIT);

  // A new calendar month always resets the free monthly allowance.
  if (stored.monthKey !== currentMonth) {
    const rolled = {
      tier,
      aiPrayersUsedThisMonth: 0,
      anonPrayersUsedEver: anonUsed,
      monthKey: currentMonth,
    };
    writeState(rolled);
    return rolled;
  }

  return {
    tier,
    aiPrayersUsedThisMonth: used,
    anonPrayersUsedEver: anonUsed,
    monthKey: currentMonth,
  };
}

/**
 * Persist the entitlements state. Never throws (a blocked storage is harmless).
 * @param {{tier: string, aiPrayersUsedThisMonth: number, anonPrayersUsedEver: number, monthKey: string}} state
 * @returns {void}
 */
export function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // A blocked storage just means the count won't persist; not worth surfacing.
  }
}

/**
 * The person's current tier.
 * @returns {'free'|'plus'}
 */
export function getTier() {
  return readState().tier;
}

/**
 * Set the tier and persist it (used after a successful purchase or restore).
 * @param {'free'|'plus'} tier
 * @returns {void}
 */
export function setTier(tier) {
  const state = readState();
  writeState({ ...state, tier: tier === 'plus' ? 'plus' : 'free' });
}

function createAnonymousClientId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
  } catch (e) {
    // Fall through to a non-sensitive random identifier.
  }
  return `beloved-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

/**
 * Stable anonymous install identifier. It contains no name, email, advertising
 * identifier, prayer text, or other personal content.
 * @returns {string}
 */
export function getPrayerClientId() {
  try {
    const existing = window.localStorage.getItem(PRAYER_CLIENT_ID_KEY);
    if (existing && /^[A-Za-z0-9_-]{16,128}$/.test(existing)) return existing;
    const created = createAnonymousClientId();
    window.localStorage.setItem(PRAYER_CLIENT_ID_KEY, created);
    return created;
  } catch (e) {
    return createAnonymousClientId();
  }
}

/**
 * Which free allowance applies to a given state: the signed-in monthly one, or
 * the once-ever try-it allowance for someone without an account.
 *
 * @param {{aiPrayersUsedThisMonth: number, anonPrayersUsedEver: number}} state
 * @param {boolean} signedIn whether a Beloved account is signed in
 * @returns {{limit: number, used: number, period: 'month'|'ever'}}
 */
function allowanceFor(state, signedIn) {
  return signedIn
    ? {
        limit: FREE_AI_PRAYER_LIMIT,
        used: state.aiPrayersUsedThisMonth,
        period: 'month',
      }
    : {
        limit: ANONYMOUS_AI_PRAYER_LIMIT,
        used: state.anonPrayersUsedEver,
        period: 'ever',
      };
}

/**
 * The free allowance in force right now, so the UI can describe it honestly
 * without restating the rule. `period` is 'month' for a signed-in person and
 * 'ever' for the try-it prayer.
 *
 * @param {boolean} signedIn whether a Beloved account is signed in
 * @returns {{limit: number, used: number, period: 'month'|'ever'}}
 */
export function freeAllowance(signedIn) {
  return allowanceFor(readState(), signedIn);
}

/**
 * How many personal prayers a free person has left: 3 a month once signed in,
 * or the single try-it prayer before that. Beloved Plus always reports
 * {@link FREE_AI_PRAYER_LIMIT} as a friendly number; the server separately
 * applies fair-use protection against automated abuse.
 *
 * @param {boolean} signedIn whether a Beloved account is signed in
 * @returns {number}
 */
export function aiPrayersRemaining(signedIn) {
  const state = readState();
  if (state.tier === 'plus') return FREE_AI_PRAYER_LIMIT;
  const { limit, used } = allowanceFor(state, signedIn);
  return Math.max(0, limit - used);
}

/**
 * Whether the person may create a personal prayer right now according to the
 * local experience. The server performs the final entitlement and fair-use
 * check. Free and signed in: yes until 3 have been used this month. Free and
 * signed out: yes until the one try-it prayer has been used, ever.
 *
 * @param {boolean} signedIn whether a Beloved account is signed in
 * @returns {boolean}
 */
export function canUseAiPrayer(signedIn) {
  const state = readState();
  if (state.tier === 'plus') return true;
  const { limit, used } = allowanceFor(state, signedIn);
  return used < limit;
}

/**
 * Record that a personal prayer was created, against whichever allowance paid
 * for it. Only the free tier counts; Plus is governed by the server's generous
 * daily fair-use allowance. Never throws.
 *
 * @param {boolean} signedIn whether a Beloved account is signed in
 * @returns {void}
 */
export function recordAiPrayerUse(signedIn) {
  const state = readState();
  if (state.tier === 'plus') return;
  writeState(
    signedIn
      ? { ...state, aiPrayersUsedThisMonth: state.aiPrayersUsedThisMonth + 1 }
      : { ...state, anonPrayersUsedEver: state.anonPrayersUsedEver + 1 }
  );
}

/**
 * Whether the developer "pretend I bought it" flag is set (web testing only).
 * @returns {boolean}
 */
function isDevPlusEnabled() {
  try {
    return window.localStorage.getItem(DEV_PLUS_FLAG) === 'true';
  } catch (e) {
    return false;
  }
}

/**
 * Whether real purchases can run: native app AND a store key is present.
 * @returns {boolean}
 */
function canPurchaseNatively() {
  return Capacitor.isNativePlatform() && REVENUECAT_IOS_KEY !== '';
}

/** Whether this browser build has RevenueCat Web Billing configured. */
export function canPurchaseOnWeb() {
  return !Capacitor.isNativePlatform() && REVENUECAT_WEB_KEY !== '';
}

/**
 * Dynamically load the RevenueCat plugin. Kept out of static imports so the web
 * bundle and jest never need the native module at run time.
 * @returns {Promise<import('@revenuecat/purchases-capacitor').PurchasesPlugin>}
 */
async function loadPurchases() {
  const mod = await import('@revenuecat/purchases-capacitor');
  return mod.Purchases;
}

let purchasesConfigurationPromise = null;

/**
 * Configure RevenueCat once, then return the plugin.
 * @returns {Promise<import('@revenuecat/purchases-capacitor').PurchasesPlugin>}
 */
async function configuredNativePurchases() {
  if (!purchasesConfigurationPromise) {
    purchasesConfigurationPromise = (async () => {
      const Purchases = await loadPurchases();
      await Purchases.configure({ apiKey: REVENUECAT_IOS_KEY });
      const result = await Purchases.getAppUserID();
      const appUserID = result && result.appUserID;
      if (typeof appUserID === 'string' && appUserID.length <= 100) {
        try {
          window.localStorage.setItem(REVENUECAT_APP_USER_ID_KEY, appUserID);
        } catch (e) {
          // A blocked storage still permits this request; it just cannot cache the id.
        }
      }
      return Purchases;
    })().catch((error) => {
      purchasesConfigurationPromise = null;
      throw error;
    });
  }
  return purchasesConfigurationPromise;
}

/** Load the browser SDK only when Web Billing is actually available. */
async function loadWebPurchases() {
  return import('@revenuecat/purchases-js');
}

let webPurchasesConfigurationPromise = null;
// Any account identify/reset invalidates store reads that began under the
// previous customer. This prevents a late anonymous refresh from overwriting a
// successful signed-in Plus result.
let purchasesIdentityRevision = 0;
// Fail closed if an SDK customer could not be detached. Local storage can be
// cleared immediately, but the configured SDK singleton may still hold the old
// custom account until a successful reset/identify or a page reload.
let purchasesIdentityUnsafe = false;

function readRevenueCatAppUserId() {
  try {
    const id = window.localStorage.getItem(REVENUECAT_APP_USER_ID_KEY) || '';
    return /^[A-Za-z0-9._$:-]{1,100}$/.test(id) ? id : '';
  } catch (e) {
    return '';
  }
}

function storeRevenueCatAppUserId(id) {
  if (typeof id !== 'string' || !/^[A-Za-z0-9._$:-]{1,100}$/.test(id)) return;
  try {
    window.localStorage.setItem(REVENUECAT_APP_USER_ID_KEY, id);
  } catch (e) {
    // A blocked storage may prevent persistence, but checkout can still proceed.
  }
}

function clearRevenueCatAppUserId() {
  try {
    window.localStorage.removeItem(REVENUECAT_APP_USER_ID_KEY);
  } catch (e) {
    // A blocked store cannot retain the identifier either.
  }
}

function isAnonymousRevenueCatId(id) {
  return typeof id === 'string' && id.startsWith('$RCAnonymousID:');
}

/** Configure RevenueCat's browser singleton with a stable anonymous customer. */
async function configuredWebPurchases() {
  if (!webPurchasesConfigurationPromise) {
    webPurchasesConfigurationPromise = (async () => {
      const { Purchases } = await loadWebPurchases();
      let appUserId = readRevenueCatAppUserId();
      if (!appUserId) appUserId = Purchases.generateRevenueCatAnonymousAppUserId();
      const purchases = Purchases.isConfigured()
        ? Purchases.getSharedInstance()
        : Purchases.configure({ apiKey: REVENUECAT_WEB_KEY, appUserId });
      storeRevenueCatAppUserId(purchases.getAppUserId());
      return purchases;
    })().catch((error) => {
      webPurchasesConfigurationPromise = null;
      throw error;
    });
  }
  return webPurchasesConfigurationPromise;
}

/**
 * Link RevenueCat to the signed-in Beloved account. RevenueCat aliases an
 * existing anonymous customer on first sign-in, preserving a purchase made
 * before the account was connected.
 *
 * @param {string} accountId Supabase's non-personal user UUID
 * The account id is persisted only after RevenueCat confirms that the linked
 * customer still owns Beloved Plus. If linking fails, the previous anonymous
 * identity and local Plus access are restored.
 *
 * @returns {Promise<{status: 'success'|'none'|'unavailable'|'error'}>}
 */
export async function identifyPurchasesUser(accountId) {
  if (typeof accountId !== 'string' || !/^[A-Za-z0-9._$:-]{1,100}$/.test(accountId)) {
    return { status: 'error' };
  }
  if (!canPurchaseNatively() && !canPurchaseOnWeb()) return { status: 'unavailable' };
  purchasesIdentityRevision += 1;

  const tierBeforeLink = getTier();
  const storedIdentityBeforeLink = readRevenueCatAppUserId();
  if (
    storedIdentityBeforeLink &&
    !isAnonymousRevenueCatId(storedIdentityBeforeLink) &&
    storedIdentityBeforeLink !== accountId
  ) {
    // Switching from account A to account B must fail closed immediately; no
    // paid call may borrow A's cached entitlement while RevenueCat is in flight.
    purchasesIdentityUnsafe = true;
    setTier('free');
  }
  let previousId = '';
  let purchases = null;

  try {
    let customerInfo;
    if (canPurchaseNatively()) {
      const Purchases = await configuredNativePurchases();
      purchases = Purchases;
      const previousResult = await Purchases.getAppUserID();
      previousId = (previousResult && previousResult.appUserID) || '';
      const result = await Purchases.logIn({ appUserID: accountId });
      customerInfo = result && result.customerInfo;
    } else {
      purchases = await configuredWebPurchases();
      previousId = purchases.getAppUserId() || '';
      if (purchases.getAppUserId() === accountId) {
        customerInfo = await purchases.getCustomerInfo();
      } else {
        const result = await purchases.identifyUser(accountId);
        customerInfo = result && result.customerInfo;
      }
    }

    if (!hasActiveEntitlement(customerInfo)) {
      const canSafelyRestoreAnonymous =
        isAnonymousRevenueCatId(previousId) && previousId !== accountId;
      if (canSafelyRestoreAnonymous) {
        let restoredCustomerInfo = null;
        try {
          if (canPurchaseNatively()) {
            const restored = await purchases.logIn({ appUserID: previousId });
            restoredCustomerInfo = restored && restored.customerInfo;
          } else {
            const restored = await purchases.changeUser(previousId);
            restoredCustomerInfo = restored && restored.customerInfo
              ? restored.customerInfo
              : restored;
          }
          // A successful inactive RevenueCat response is authoritative. Never
          // resurrect an expired anonymous subscription from local storage.
          purchasesIdentityUnsafe = false;
          setTier(hasActiveEntitlement(restoredCustomerInfo) ? 'plus' : 'free');
        } catch (restoreError) {
          // The SDK may now be attached to the attempted account while storage
          // still names the anonymous customer. Block all paid operations until
          // a reload or later successful identify/reset repairs that mismatch.
          purchasesIdentityUnsafe = true;
          setTier('free');
        }
      }
      if (canSafelyRestoreAnonymous) {
        // The verified anonymous result above decides whether device access is
        // still active; cached Plus alone is never sufficient after a
        // successful inactive response.
      } else {
        // A different custom account must never lend its cached Plus access to
        // this account. The successful identify result is authoritative even
        // when it is free or expired.
        storeRevenueCatAppUserId(accountId);
        purchasesIdentityUnsafe = false;
        setTier('free');
      }
      return { status: 'none' };
    }

    // Persisting the account id is deliberately last: `identifyUser` may have
    // changed the in-memory SDK customer, but access is not considered safe to
    // move until the returned customer info proves the entitlement is active.
    storeRevenueCatAppUserId(accountId);
    purchasesIdentityUnsafe = false;
    setTier('plus');
    return { status: 'success' };
  } catch (error) {
    if (previousId === accountId) {
      // A transient read failure is not proof that the current account lost
      // access. Preserve its identity/cache and let the UI offer a retry.
      setTier(tierBeforeLink);
      purchasesIdentityUnsafe = false;
    } else if (purchases && isAnonymousRevenueCatId(previousId)) {
      try {
        if (canPurchaseNatively()) {
          await purchases.logIn({ appUserID: previousId });
        } else {
          await purchases.changeUser(previousId);
        }
        purchasesIdentityUnsafe = false;
        // Unlike a successful inactive entitlement response, this branch is a
        // genuine transport/SDK error, so preserving the last verified local
        // tier is the least disruptive safe fallback after rollback succeeds.
        setTier(tierBeforeLink);
      } catch (restoreError) {
        purchasesIdentityUnsafe = true;
        setTier('free');
      }
    } else {
      // If the prior customer was another account, detach it rather than
      // exposing that account's entitlement under the newly signed-in UI.
      await resetPurchasesUser();
      setTier('free');
    }
    return { status: 'error' };
  }
}

/** Leave the signed-in RevenueCat customer and return to a fresh anonymous id. */
export async function resetPurchasesUser() {
  if (!canPurchaseNatively() && !canPurchaseOnWeb()) return { status: 'unavailable' };
  purchasesIdentityRevision += 1;
  let currentIdBeforeReset = readRevenueCatAppUserId();
  if (currentIdBeforeReset && !isAnonymousRevenueCatId(currentIdBeforeReset)) {
    // Close the cross-account window before any SDK/network await. The flag
    // blocks checkout, restore and subscriber identity until detach succeeds.
    purchasesIdentityUnsafe = true;
    setTier('free');
  }
  try {
    let anonymousId = '';
    let preserveAnonymousTier = false;
    if (canPurchaseNatively()) {
      const Purchases = await configuredNativePurchases();
      const current = await Purchases.getAppUserID();
      const currentId = (current && current.appUserID) || '';
      currentIdBeforeReset = currentId;
      if (isAnonymousRevenueCatId(currentId)) {
        // RevenueCat rejects logOut for an already-anonymous customer. It is
        // already safely detached from the Supabase account, so keep it.
        anonymousId = currentId;
        const existing = await Purchases.getCustomerInfo();
        preserveAnonymousTier = hasActiveEntitlement(existing && existing.customerInfo);
      } else {
        await Purchases.logOut();
        const result = await Purchases.getAppUserID();
        anonymousId = (result && result.appUserID) || '';
      }
    } else {
      const { Purchases } = await loadWebPurchases();
      const purchases = await configuredWebPurchases();
      const currentId = purchases.getAppUserId() || '';
      currentIdBeforeReset = currentId;
      if (isAnonymousRevenueCatId(currentId)) {
        anonymousId = currentId;
        preserveAnonymousTier = hasActiveEntitlement(await purchases.getCustomerInfo());
      } else {
        anonymousId = Purchases.generateRevenueCatAnonymousAppUserId();
        await purchases.changeUser(anonymousId);
      }
    }
    storeRevenueCatAppUserId(anonymousId);
    purchasesIdentityUnsafe = false;
    setTier(preserveAnonymousTier ? 'plus' : 'free');
    return { status: 'success', preservedAnonymous: preserveAnonymousTier };
  } catch (error) {
    if (currentIdBeforeReset && !isAnonymousRevenueCatId(currentIdBeforeReset)) {
      clearRevenueCatAppUserId();
      purchasesIdentityUnsafe = true;
      setTier('free');
    }
    return { status: 'error' };
  }
}

/**
 * Identity sent with a personal-prayer request. The client id is anonymous and
 * used for fair use; the RevenueCat App User ID lets the server verify Plus.
 * @returns {Promise<{client_id: string, subscriber_id?: string}>}
 */
export async function getPrayerAccessIdentity() {
  const identity = { client_id: getPrayerClientId() };
  let subscriberId = '';

  try {
    subscriberId = window.localStorage.getItem(REVENUECAT_APP_USER_ID_KEY) || '';
  } catch (e) {
    subscriberId = '';
  }

  if (purchasesIdentityUnsafe) return identity;

  if (!subscriberId && canPurchaseNatively()) {
    try {
      const Purchases = await configuredNativePurchases();
      const result = await Purchases.getAppUserID();
      subscriberId = (result && result.appUserID) || '';
    } catch (e) {
      subscriberId = '';
    }
  }

  if (!subscriberId && canPurchaseOnWeb()) {
    try {
      const purchases = await configuredWebPurchases();
      subscriberId = purchases.getAppUserId() || '';
    } catch (e) {
      subscriberId = '';
    }
  }

  if (subscriberId && /^[A-Za-z0-9._$:-]{1,100}$/.test(subscriberId)) {
    identity.subscriber_id = subscriberId;
  }
  return identity;
}

/**
 * True when a RevenueCat customer info object shows any active entitlement.
 * @param {{entitlements?: {active?: Record<string, unknown>}}} customerInfo
 * @returns {boolean}
 */
function hasActiveEntitlement(customerInfo) {
  const active = customerInfo && customerInfo.entitlements && customerInfo.entitlements.active;
  return !!active && !!active[ENTITLEMENT_ID];
}

/**
 * True when a store error means this customer already owns the product.
 * Web (purchases-js): PurchasesError.errorCode 6 = ProductAlreadyPurchasedError.
 * Native (purchases-capacitor): code 6 / readableErrorCode PRODUCT_ALREADY_PURCHASED.
 * @param {unknown} e a rejected store error
 * @returns {boolean}
 */
export function isAlreadyOwnedError(e) {
  if (!e || typeof e !== 'object') return false;
  if (e.errorCode === 6 || e.code === 6 || e.code === '6') return true;
  const readable = String(e.readableErrorCode || '').toUpperCase();
  return readable === 'PRODUCT_ALREADY_PURCHASED';
}

function packageProduct(pkg) {
  return pkg && (pkg.product || pkg.webBillingProduct || pkg.rcBillingProduct);
}

/**
 * Find the RevenueCat package for a product id within the Beloved Plus offering.
 * @param {object} offerings the result of `getOfferings()`
 * @param {string} productId `plus_annual` or `plus_monthly`
 * @returns {object|null} the matching package, or null
 */
function findPackage(offerings, productId) {
  const offering =
    (offerings && offerings.all && offerings.all[OFFERING_ID]) ||
    (offerings && offerings.current) ||
    null;
  const packages = (offering && offering.availablePackages) || [];
  const standardPackage =
    productId === PRODUCT_ANNUAL
      ? offering && offering.annual
      : offering && offering.monthly;
  return (
    standardPackage ||
    packages.find((p) => packageProduct(p) && packageProduct(p).identifier === productId) ||
    packages.find((p) => p.identifier === productId) ||
    packages.find((p) =>
      productId === PRODUCT_ANNUAL
        ? p.packageType === '$rc_annual'
        : p.packageType === '$rc_monthly'
    ) ||
    null
  );
}

function normalizeWebProduct(product) {
  if (!product || !product.price) return null;
  const base = product.defaultSubscriptionOption && product.defaultSubscriptionOption.base;
  const intro = product.introPricePhase;
  const price = Number(product.price.amountMicros) / 1000000;
  const pricePerMonth = base && base.pricePerMonth;
  return {
    identifier: product.identifier,
    price,
    priceString: product.price.formattedPrice,
    pricePerMonthString:
      (pricePerMonth && pricePerMonth.formattedPrice) ||
      formatStorePrice(price / 12, product.price.currency, product.price.formattedPrice),
    currencyCode: product.price.currency,
    introPrice:
      intro && intro.price
        ? {
            price: Number(intro.price.amountMicros) / 1000000,
            priceString: intro.price.formattedPrice,
            cycles: intro.cycleCount,
            period: intro.periodDuration,
            periodUnit: intro.period && String(intro.period.unit || '').toUpperCase(),
          }
        : null,
  };
}

function formatStorePrice(value, currencyCode, fallback) {
  if (!Number.isFinite(value) || !currencyCode) return fallback;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    return fallback;
  }
}

/**
 * Convert RevenueCat products into the small display model used by the
 * paywall. Exported for a focused unit test; it performs no store calls.
 *
 * @param {object} annualProduct
 * @param {object} monthlyProduct
 * @param {boolean} introEligible
 * @returns {Array<object>}
 */
export function buildPlusPlanDisplay(annualProduct, monthlyProduct, introEligible = false) {
  if (!annualProduct || !monthlyProduct) return FALLBACK_PLUS_PLANS;

  const intro = introEligible ? annualProduct.introPrice : null;
  const isFoundingYear =
    intro &&
    Number(intro.price) > 0 &&
    Number(intro.cycles) === 1 &&
    (intro.period === 'P1Y' || intro.periodUnit === 'YEAR');
  const annualHeadline = isFoundingYear
    ? intro.priceString
    : annualProduct.priceString;
  const annualBilling = isFoundingYear
    ? `Then ${annualProduct.priceString}/year \u00B7 renews annually`
    : annualProduct.pricePerMonthString
      ? `${annualProduct.pricePerMonthString}/month equivalent \u00B7 renews annually`
      : `Renews annually at ${annualProduct.priceString}`;
  const comparisonAnnualPrice = isFoundingYear
    ? Number(intro.price)
    : Number(annualProduct.price);
  const monthlyAnnualPrice = Number(monthlyProduct.price) * 12;
  const savingPercent =
    Number.isFinite(comparisonAnnualPrice) &&
    Number.isFinite(monthlyAnnualPrice) &&
    monthlyAnnualPrice > 0
      ? Math.max(0, Math.round((1 - comparisonAnnualPrice / monthlyAnnualPrice) * 100))
      : 0;

  return [
    {
      id: PRODUCT_ANNUAL,
      headlinePrice: annualHeadline,
      cadence: isFoundingYear ? 'first year' : 'a year',
      billing: annualBilling,
      checkoutLabel: isFoundingYear
        ? `Continue \u2014 ${intro.priceString} for first year`
        : `Continue \u2014 ${annualProduct.priceString}/year`,
      saving: isFoundingYear
        ? 'FOUNDING OFFER'
        : savingPercent > 0
          ? `SAVE ${savingPercent}%`
          : null,
      best: true,
    },
    {
      id: PRODUCT_MONTHLY,
      headlinePrice: monthlyProduct.priceString,
      cadence: 'a month',
      billing: 'Renews monthly',
      checkoutLabel: `Continue \u2014 ${monthlyProduct.priceString}/month`,
      saving: null,
      best: false,
    },
  ];
}

/**
 * Fetch localized Plus prices from the native store. If the store is not
 * available, the paywall keeps its transparent UK launch fallback.
 *
 * @returns {Promise<Array<object>>}
 */
export async function getPlusPlanPricing() {
  if (!canPurchaseNatively() && !canPurchaseOnWeb()) return FALLBACK_PLUS_PLANS;

  try {
    const purchases = canPurchaseNatively()
      ? await configuredNativePurchases()
      : await configuredWebPurchases();
    const offerings = await purchases.getOfferings();
    const annualPackage = findPackage(offerings, PRODUCT_ANNUAL);
    const monthlyPackage = findPackage(offerings, PRODUCT_MONTHLY);
    if (!annualPackage || !monthlyPackage) return FALLBACK_PLUS_PLANS;

    if (canPurchaseOnWeb()) {
      const annualProduct = normalizeWebProduct(packageProduct(annualPackage));
      const monthlyProduct = normalizeWebProduct(packageProduct(monthlyPackage));
      return buildPlusPlanDisplay(
        annualProduct,
        monthlyProduct,
        !!(annualProduct && annualProduct.introPrice)
      );
    }

    let introEligible = false;
    try {
      const eligibility = await purchases.checkTrialOrIntroductoryPriceEligibility({
        productIdentifiers: [annualPackage.product.identifier],
      });
      // RevenueCat INTRO_ELIGIBILITY_STATUS_ELIGIBLE.
      introEligible =
        eligibility &&
        eligibility[annualPackage.product.identifier] &&
        eligibility[annualPackage.product.identifier].status === 2;
    } catch (e) {
      // Unknown eligibility must show the standard price to avoid misleading.
      introEligible = false;
    }

    return buildPlusPlanDisplay(
      annualPackage.product,
      monthlyPackage.product,
      introEligible
    );
  } catch (e) {
    return FALLBACK_PLUS_PLANS;
  }
}

/**
 * Buy Beloved Plus.
 *
 * Resolves (never rejects) with a status the paywall can act on:
 * - `success`     — Plus is now on (tier set to `plus`)
 * - `unavailable` — no store here (web, or no key yet); show the in-app message
 * - `cancelled`   — the person backed out of the store sheet
 * - `error`       — something went wrong; a gentle retry message is fine
 *
 * On the web, the developer flag turns this into an instant success for testing.
 *
 * @param {string} [product] `plus_annual` (default) or `plus_monthly`
 * @param {string} [customerEmail] signed-in account email for the web receipt
 * @returns {Promise<{status: 'success'|'unavailable'|'cancelled'|'error'}>}
 */
export async function purchase(product = PRODUCT_ANNUAL, customerEmail = '') {
  // Web testing shortcut: pretend the purchase succeeded.
  if (isDevPlusEnabled()) {
    setTier('plus');
    return { status: 'success' };
  }
  if (purchasesIdentityUnsafe) return { status: 'error' };

  // No configured store on this platform yet.
  if (!canPurchaseNatively() && !canPurchaseOnWeb()) {
    return { status: 'unavailable' };
  }

  try {
    const purchases = canPurchaseNatively()
      ? await configuredNativePurchases()
      : await configuredWebPurchases();
    const offerings = await purchases.getOfferings();
    const pkg = findPackage(offerings, product);
    if (!pkg) return { status: 'unavailable' };

    const result = canPurchaseNatively()
      ? await purchases.purchasePackage({ aPackage: pkg })
      : await purchases.purchase({
          rcPackage: pkg,
          customerEmail: customerEmail || undefined,
          selectedLocale: 'en-GB',
          skipSuccessPage: true,
        });
    if (hasActiveEntitlement(result && result.customerInfo)) {
      setTier('plus');
      return { status: 'success', redemptionInfo: result.redemptionInfo || null };
    }
    // Checkout resolved, so payment evidence exists even though the
    // entitlement is not visible yet (propagation, or a dashboard gap like
    // the 2026-08-03 incident). Callers must treat this as paid.
    return { status: 'paid_pending' };
  } catch (e) {
    // RevenueCat reports a user cancel via a flag on the error.
    if (e && (e.userCancelled || e.errorCode === 1)) return { status: 'cancelled' };
    if (isAlreadyOwnedError(e)) return { status: 'already_owned' };
    return { status: 'error' };
  }
}

/** Refresh the locally cached tier from the store without opening checkout. */
export async function refreshTier() {
  if (isDevPlusEnabled()) {
    setTier('plus');
    return { status: 'success' };
  }
  if (purchasesIdentityUnsafe) return { status: 'error' };
  if (!canPurchaseNatively() && !canPurchaseOnWeb()) return { status: 'unavailable' };
  const identityRevisionAtStart = purchasesIdentityRevision;
  try {
    let customerInfo;
    if (canPurchaseNatively()) {
      const Purchases = await configuredNativePurchases();
      const result = await Purchases.getCustomerInfo();
      customerInfo = result && result.customerInfo;
    } else {
      const purchases = await configuredWebPurchases();
      customerInfo = await purchases.getCustomerInfo();
    }
    const active = hasActiveEntitlement(customerInfo);
    if (identityRevisionAtStart !== purchasesIdentityRevision) {
      return { status: getTier() === 'plus' ? 'success' : 'none' };
    }
    setTier(active ? 'plus' : 'free');
    return { status: active ? 'success' : 'none' };
  } catch (e) {
    return { status: 'error' };
  }
}

/**
 * Return RevenueCat's customer portal / App Store subscription-management URL.
 * Only trusted https URLs are surfaced to the UI.
 *
 * @returns {Promise<{status: 'success', url: string}|{status: 'none'|'unavailable'|'error'}>}
 */
export async function getSubscriptionManagementUrl() {
  if (purchasesIdentityUnsafe) return { status: 'error' };
  if (!canPurchaseNatively() && !canPurchaseOnWeb()) return { status: 'unavailable' };

  try {
    let customerInfo;
    if (canPurchaseNatively()) {
      const Purchases = await configuredNativePurchases();
      const result = await Purchases.getCustomerInfo();
      customerInfo = result && result.customerInfo;
    } else {
      const purchases = await configuredWebPurchases();
      customerInfo = await purchases.getCustomerInfo();
    }

    const value = customerInfo && customerInfo.managementURL;
    if (!value) return { status: 'none' };
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:') return { status: 'error' };
    return { status: 'success', url: parsed.toString() };
  } catch (error) {
    return { status: 'error' };
  }
}

/**
 * Restore a previous Beloved Plus purchase (for a person on a new device).
 *
 * Resolves (never rejects):
 * - `success`     — an active purchase was found; tier set to `plus`
 * - `none`        — the store had nothing to restore
 * - `unavailable` — no store here (web, or no key yet)
 * - `error`       — something went wrong; a gentle retry message is fine
 *
 * @returns {Promise<{status: 'success'|'none'|'unavailable'|'error'}>}
 */
export async function restore() {
  if (isDevPlusEnabled()) {
    setTier('plus');
    return { status: 'success' };
  }
  if (purchasesIdentityUnsafe) return { status: 'error' };

  if (!canPurchaseNatively() && !canPurchaseOnWeb()) {
    return { status: 'unavailable' };
  }

  try {
    let customerInfo;
    if (canPurchaseNatively()) {
      const Purchases = await configuredNativePurchases();
      const result = await Purchases.restorePurchases();
      customerInfo = result && result.customerInfo;
    } else {
      const purchases = await configuredWebPurchases();
      customerInfo = await purchases.getCustomerInfo();
    }
    if (hasActiveEntitlement(customerInfo)) {
      setTier('plus');
      return { status: 'success' };
    }
    return { status: 'none' };
  } catch (e) {
    return { status: 'error' };
  }
}
